/** XeAgents.ai**/
import { useState, useEffect, useMemo } from "react";
import { useVoiceEngine, visibleAgents } from "@/lib/voice-engine";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, Loader2, CheckCircle2, Target, Plus, Trash2, Clock, Globe, Calendar, Download, AlertCircle } from "lucide-react";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthStorage } from "@/lib/auth-storage";
import { Switch } from "@/components/ui/switch";
import { countryTimezones, getCurrentTimeInTimezone } from "@/lib/country-timezones";
import { TimezoneEnforcementModal } from "@/components/TimezoneEnforcementModal";
import { PhoneConflictDialog, PhoneConflictState, initialPhoneConflictState } from "./PhoneConflictDialog";

interface Agent {
  id: string;
  name: string;
  personality: string;
  type: 'incoming' | 'flow';
  isActive?: boolean;
  voiceProvider?: string;
  phoneNumberType?: 'twilio' | 'plivo';
  connectedPhoneNumber?: { id: string; phoneNumber: string; friendlyName?: string; type?: 'twilio' | 'plivo' } | null;
  flowId?: string;
}

interface FlowVariable {
  name: string;
  foundIn: string;
  context: string;
  category?: 'contact' | 'campaign' | 'captured' | 'standard';
  required?: boolean;
}

interface FlowVariablesResponse {
  flowId: string;
  flowName: string;
  variables: FlowVariable[];  // All variables (for backwards compatibility)
  contactVariables: FlowVariable[];  // From CSV (per-contact) - REQUIRED
  campaignVariables: FlowVariable[];  // Global settings (agent/campaign level)
  capturedVariables: FlowVariable[];  // Captured during the call
  standardVariables: { name: string; description: string; source: string }[];
}

interface UserData {
  id: number;
  email: string;
  name: string;
  timezone?: string | null;
  billingCountry?: string | null;
}

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}


export function CreateCampaignDialog({ open, onOpenChange }: CreateCampaignDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [animationState, setAnimationState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: "",
    type: "Lead Qualification",
    goal: "",
    script: "",
    agentId: "",
    transferNumber: "",
    transferKeywords: [] as string[],
    // Scheduling fields
    scheduleEnabled: false,
    scheduleCountry: "",
    scheduleTimezone: "",
    scheduledDateTime: "", // Local datetime string in format "YYYY-MM-DDTHH:mm"
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvValidationError, setCsvValidationError] = useState<string | null>(null);
  const [csvMissingColumns, setCsvMissingColumns] = useState<string[]>([]);
  const [manualContacts, setManualContacts] = useState<Array<{firstName: string; lastName: string; phone: string; email: string; customFields?: Record<string, string>}>>([]);
  const [currentContact, setCurrentContact] = useState({firstName: "", lastName: "", phone: "", email: "", customFields: {} as Record<string, string>});
  const [customFieldKey, setCustomFieldKey] = useState("");
  const [customFieldValue, setCustomFieldValue] = useState("");
  const [step, setStep] = useState(1);
  const [conflictDialog, setConflictDialog] = useState<PhoneConflictState>(initialPhoneConflictState);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);

  const { data: userData } = useQuery<UserData>({
    queryKey: ["/api/auth/me"],
    enabled: open,
  });

  useEffect(() => {
    if (open && userData) {
      if (!userData.timezone) {
        setShowTimezoneModal(true);
      }
    }
  }, [open, userData]);

  const handleTimezoneSet = () => {
    setShowTimezoneModal(false);
    queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
  };

  const { data: allAgents = [] } = useQuery<Agent[]>({
    queryKey: ["/api/agents"],
    enabled: open,
  });
  // With Nova Sonic selected by the super admin, agents that run on ElevenLabs are not offered
  const { novaSelected } = useVoiceEngine();
  const agents = useMemo(() => visibleAgents(allAgents, novaSelected), [allAgents, novaSelected]);

  // Get the selected agent's flowId
  const selectedAgent = agents.find(a => a.id === formData.agentId);
  const selectedFlowId = selectedAgent?.flowId;

  // Fetch flow variables when agent is selected
  const { data: flowVariables } = useQuery<FlowVariablesResponse>({
    queryKey: ["/api/flow-automation/flows", selectedFlowId, "variables"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/flow-automation/flows/${selectedFlowId}/variables`);
      return res.json();
    },
    enabled: !!selectedFlowId,
  });

  const createMutation = useMutation({
    mutationFn: async (finalContacts: Array<{firstName: string; lastName: string; phone: string; email: string}>) => {
      // Build the campaign data with proper scheduling conversion
      const campaignData: Record<string, unknown> = {
        name: formData.name,
        type: formData.type,
        goal: formData.goal,
        script: formData.script,
        agentId: formData.agentId,
        transferNumber: formData.transferNumber,
        transferKeywords: formData.transferKeywords,
      };

      // Always send the timezone, scheduled or not - the server rejects a campaign without one.
      campaignData.scheduleTimezone = formData.scheduleTimezone;

      // Handle scheduling if enabled
      if (formData.scheduleEnabled && formData.scheduledDateTime && formData.scheduleTimezone) {
        // Convert local datetime to UTC timestamp using the selected timezone
        const [datePart, timePart] = formData.scheduledDateTime.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);

        // Create a date in UTC that represents the local time in the selected timezone
        const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

        // Get the offset difference between local and target timezone
        const localTimeStr = localDate.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
        const targetTimeStr = localDate.toLocaleString('en-US', { timeZone: formData.scheduleTimezone });
        const localTime = new Date(localTimeStr);
        const targetTime = new Date(targetTimeStr);
        const offsetDiff = targetTime.getTime() - localTime.getTime();

        // Calculate the UTC timestamp for the scheduled time in the target timezone
        const scheduledForUtc = new Date(localDate.getTime() - offsetDiff);

        campaignData.scheduledFor = scheduledForUtc.toISOString();
        campaignData.scheduleTimezone = formData.scheduleTimezone;
        campaignData.status = 'scheduled'; // Set status to scheduled
      }

      const res = await apiRequest("POST", "/api/campaigns", campaignData);
      return res.json();
    },
    onSuccess: async (campaign, finalContacts) => {
      if (csvFile) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", csvFile);

        try {
          const uploadHeaders: Record<string, string> = {};
          const authHeader = AuthStorage.getAuthHeader();
          if (authHeader) {
            uploadHeaders["Authorization"] = authHeader;
          }

          const res = await fetch(`/api/campaigns/${campaign.id}/contacts/upload`, {
            method: "POST",
            headers: uploadHeaders,
            body: formDataUpload,
          });

          if (!res.ok) {
            throw new Error("Failed to upload CSV contacts");
          }
        } catch (error) {
          toast({
            title: t("campaigns.toast.csvUploadFailed"),
            description: t("campaigns.toast.csvUploadFailedDesc"),
            variant: "destructive",
          });
        }
      }

      if (finalContacts.length > 0) {
        try {
          const contacts = finalContacts.map(c => ({
            firstName: c.firstName,
            lastName: c.lastName,
            phone: c.phone,
            email: c.email,
            customFields: c.customFields || {},
          }));

          const res = await apiRequest("POST", `/api/campaigns/${campaign.id}/contacts/bulk`, { contacts });

          if (!res.ok) {
            throw new Error("Failed to add manual contacts");
          }

          toast({ title: t("campaigns.toast.manualContactsSuccess") });
        } catch (error) {
          toast({
            title: t("campaigns.toast.manualContactsFailed"),
            variant: "destructive",
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      setAnimationState('success');
      toast({ title: t("campaigns.toast.createdSuccess") });
      setTimeout(() => {
        setAnimationState('idle');
        handleClose();
      }, 1500);
    },
    onError: (error: any) => {
      setAnimationState('error');
      setTimeout(() => setAnimationState('idle'), 600);
      
      // Check for phone conflict (409)
      if (error.status === 409 || error.conflictType) {
        setConflictDialog({
          isOpen: true,
          title: error.error || "Phone Number Conflict",
          message: error.message || error.error || "This phone number has a conflict.",
          conflictType: error.conflictType,
          connectedAgentName: error.connectedAgentName,
          campaignName: error.campaignName,
        });
        return;
      }
      
      toast({
        title: t("campaigns.toast.createFailed"),
        description: error.message || t("common.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const resetFormState = () => {
    setFormData({
      name: "",
      type: "Lead Qualification",
      goal: "",
      script: "",
      agentId: "",
      transferNumber: "",
      transferKeywords: [],
      scheduleEnabled: false,
      scheduleCountry: "",
      scheduleTimezone: "",
      scheduledDateTime: "",
    });
    setCsvFile(null);
    setCsvValidationError(null);
    setCsvMissingColumns([]);
    setCustomFieldKey("");
    setCustomFieldValue("");
    setManualContacts([]);
    setCurrentContact({firstName: "", lastName: "", phone: "", email: "", customFields: {}});
    setStep(1);
    setAnimationState('idle');
  };

  const handleAddContact = () => {
    if (!currentContact.firstName || !currentContact.phone) {
      toast({
        title: t("campaigns.toast.invalidContact"),
        description: t("campaigns.toast.firstNamePhoneRequired"),
        variant: "destructive"
      });
      return;
    }

    // Note: Contact-level flow variables are optional for personalization
    // The AI will adapt naturally if these fields are not provided

    setManualContacts([...manualContacts, currentContact]);
    toast({
      title: t("campaigns.toast.contactAdded"),
      description: t("campaigns.toast.contactAddedDesc", { name: currentContact.firstName })
    });
    setCurrentContact({firstName: "", lastName: "", phone: "", email: "", customFields: {}});
  };

  const handleRemoveContact = (index: number) => {
    setManualContacts(manualContacts.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    resetFormState();
    onOpenChange(false);
  };

  const handleNext = () => {
    if (step === 1 && !formData.name) {
      toast({ title: t("campaigns.toast.pleaseEnterName"), variant: "destructive" });
      return;
    }
    // Timezone is required for every campaign, scheduled or not - it also governs the times
    // the agent offers when booking, so "not scheduling this one" is not a reason to skip it.
    if (step === 1 && !formData.scheduleCountry) {
      toast({ title: t("campaigns.toast.selectCountry", "Please select the country your contacts are in"), variant: "destructive" });
      return;
    }
    if (step === 1 && !formData.scheduleTimezone) {
      toast({ title: t("campaigns.toast.selectTimezone", "Please select a timezone for this campaign"), variant: "destructive" });
      return;
    }

    // Validate scheduling fields if enabled
    if (step === 1 && formData.scheduleEnabled) {
      if (!formData.scheduledDateTime) {
        toast({ title: t("campaigns.toast.selectDateTime", "Please select a date and time for scheduling"), variant: "destructive" });
        return;
      }
      // Validate that the scheduled time is in the future
      const scheduledDate = new Date(formData.scheduledDateTime);
      if (scheduledDate <= new Date()) {
        toast({ title: t("campaigns.toast.futureTimeRequired", "Please select a future date and time"), variant: "destructive" });
        return;
      }
    }
    if (step === 2 && !formData.agentId) {
      toast({ title: t("campaigns.toast.pleaseSelectAgent"), variant: "destructive" });
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = () => {
    if (!formData.agentId) {
      toast({ title: t("campaigns.toast.pleaseSelectAgent"), variant: "destructive" });
      return;
    }
    // Build final contacts list, auto-including any unsaved current contact
    const finalContacts = [...manualContacts];
    if (currentContact.firstName && currentContact.phone) {
      // Contact-level flow variables are optional - AI will adapt if not provided
      finalContacts.push(currentContact);
    }
    createMutation.mutate(finalContacts);
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      resetFormState();
    }
  };

  // Generate and download dynamic CSV template based on flow variables
  // Only include CONTACT-level variables (per-contact from CSV), not captured or campaign variables
  const handleDownloadTemplate = () => {
    const baseColumns = ['phone_number', 'first_name', 'last_name'];
    // Only use contactVariables (per-contact), not all variables
    // Captured variables are collected during the call, campaign variables are set globally
    const flowColumns = flowVariables?.contactVariables?.map(v => v.name) || [];
    const allColumns = [...baseColumns, ...flowColumns];

    // Create header row
    const headerRow = allColumns.join(',');

    // Create example data row with helpful placeholders
    const exampleData = [
      '+12345678900',
      'John',
      'Smith',
      ...flowColumns.map(col => `[${col}]`)
    ].join(',');

    // Create CSV content
    const csvContent = `${headerRow}\n${exampleData}\n`;

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `campaign_template_${selectedAgent?.name?.replace(/\s+/g, '_') || 'custom'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Validate CSV file for required columns
  const handleCsvFileChange = async (file: File | null) => {
    setCsvValidationError(null);
    setCsvMissingColumns([]);

    if (!file) {
      setCsvFile(null);
      return;
    }

    // Required: phone_number and first_name
    // Other fields (last_name, custom fields) are optional for personalization
    const requiredBaseColumns = ['phone_number', 'first_name'];
    // Note: Flow variables (contact, campaign, captured) are all optional
    const allRequiredColumns = [...requiredBaseColumns];

    try {
      // Read the first line of the file to get headers
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        setCsvValidationError("The file appears to be empty.");
        setCsvFile(null);
        return;
      }

      // Parse header row (handle both CSV and potential Excel-exported CSV)
      const headerLine = lines[0];
      const headers = headerLine
        .split(',')
        .map(h => h.trim().toLowerCase().replace(/^["']|["']$/g, ''));

      // Check for missing required columns
      const missingColumns: string[] = [];

      for (const required of allRequiredColumns) {
        const found = headers.some(h =>
          h === required ||
          h === required.replace(/_/g, '') ||
          h.replace(/[_\s]/g, '') === required.replace(/[_\s]/g, '')
        );
        if (!found) {
          missingColumns.push(required);
        }
      }

      if (missingColumns.length > 0) {
        setCsvValidationError(`Missing required columns in your CSV file.`);
        setCsvMissingColumns(missingColumns);
        setCsvFile(null);
        toast({
          title: "CSV Validation Failed",
          description: `Missing columns: ${missingColumns.join(', ')}. Please download the template and try again.`,
          variant: "destructive"
        });
        return;
      }

      // File is valid
      setCsvFile(file);
      toast({
        title: "CSV Validated",
        description: `File contains all required columns. ${lines.length - 1} contacts found.`
      });
    } catch (error) {
      setCsvValidationError("Failed to read the file. Please ensure it's a valid CSV file.");
      setCsvFile(null);
    }
  };

  return (
    <>
      <TimezoneEnforcementModal
        open={showTimezoneModal}
        onOpenChange={(isOpen) => {
          setShowTimezoneModal(isOpen);
          if (!isOpen && !userData?.timezone) {
            onOpenChange(false);
          }
        }}
        onSuccess={handleTimezoneSet}
      />
      <Dialog open={open && !showTimezoneModal} onOpenChange={handleOpenChange}>
        <DialogContent className={`max-w-2xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden ${animationState === 'error' ? 'animate-shake' : ''}`}>
        {animationState === 'success' && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/90 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 animate-in zoom-in-50 fade-in duration-300">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <CheckCircle2 className="h-10 w-10 text-white animate-in zoom-in-75 duration-300 delay-150" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-foreground">{t("campaigns.create.successTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("campaigns.create.successSubtitle")}</p>
              </div>
            </div>
          </div>
        )}
        
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>{t("campaigns.create.title")}</DialogTitle>
              <DialogDescription>{t("campaigns.create.subtitle")}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6">
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                  step >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {s}
              </div>
              {s < 3 && <div className={`h-0.5 w-12 ${step > s ? "bg-primary" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        <div className="space-y-6 py-4">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t("campaigns.create.step1Title")}</h3>
              <div className="space-y-2">
                <Label htmlFor="campaign-name">{t("campaigns.create.nameRequired")}</Label>
                <Input
                  id="campaign-name"
                  placeholder={t("campaigns.create.namePlaceholder")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="input-campaign-name"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="campaign-type">{t("campaigns.create.typeRequired")}</Label>
                  <InfoTooltip content={t("campaigns.create.typeTooltip")} />
                </div>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger data-testid="select-campaign-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lead Qualification">{t("campaigns.create.typeOptions.lead")}</SelectItem>
                    <SelectItem value="Feedback Collection">{t("campaigns.create.typeOptions.feedback")}</SelectItem>
                    <SelectItem value="Promotional">{t("campaigns.create.typeOptions.promotional")}</SelectItem>
                    <SelectItem value="Payment Reminder">{t("campaigns.create.typeOptions.payment")}</SelectItem>
                    <SelectItem value="Event Promotion">{t("campaigns.create.typeOptions.event")}</SelectItem>
                    <SelectItem value="Survey">{t("campaigns.create.typeOptions.survey")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="campaign-goal">{t("campaigns.create.goalOptional")}</Label>
                  <InfoTooltip content={t("campaigns.create.goalTooltip")} />
                </div>
                <Textarea
                  id="campaign-goal"
                  placeholder={t("campaigns.create.goalPlaceholder")}
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  rows={3}
                  data-testid="input-campaign-goal"
                />
              </div>

              {/* Campaign Timezone - required for EVERY campaign, not only scheduled ones.
                  It decides when calls go out and, just as importantly, the clock times the
                  agent reads aloud when offering appointment slots. Left unset it used to
                  fall back to America/New_York downstream, which offered an Indian prospect
                  a demo at "three thirty AM". */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-base font-medium">
                    {t("campaigns.create.campaignTimezone", "Campaign Timezone")} <span className="text-destructive">*</span>
                  </Label>
                  <InfoTooltip content={t("campaigns.create.campaignTimezoneTooltip", "Where your contacts are. This sets when calls go out and the times the agent offers when booking a meeting.")} />
                </div>

                <div className="space-y-4 pl-6">
                  {/* Country Selection */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="schedule-country">{t("campaigns.create.selectCountry", "Select Country")}</Label>
                      </div>
                      <Select
                        value={formData.scheduleCountry}
                        onValueChange={(value) => {
                          const selectedCountry = countryTimezones.find(c => c.code === value);
                          const firstTimezone = selectedCountry?.timezones[0]?.value || "";
                          setFormData({
                            ...formData,
                            scheduleCountry: value,
                            scheduleTimezone: firstTimezone,
                          });
                        }}
                      >
                        <SelectTrigger data-testid="select-schedule-country">
                          <SelectValue placeholder={t("campaigns.create.selectCountryPlaceholder", "Choose a country")} />
                        </SelectTrigger>
                        <SelectContent>
                          {countryTimezones.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Timezone Selection */}
                    {formData.scheduleCountry && (
                      <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="schedule-timezone">{t("campaigns.create.selectTimezone", "Select Timezone")}</Label>
                        </div>
                        <Select
                          value={formData.scheduleTimezone}
                          onValueChange={(value) => setFormData({ ...formData, scheduleTimezone: value })}
                        >
                          <SelectTrigger data-testid="select-schedule-timezone">
                            <SelectValue placeholder={t("campaigns.create.selectTimezonePlaceholder", "Choose a timezone")} />
                          </SelectTrigger>
                          <SelectContent>
                            {countryTimezones
                              .find(c => c.code === formData.scheduleCountry)
                              ?.timezones.map((tz) => (
                                <SelectItem key={tz.value} value={tz.value}>
                                  {tz.name} ({tz.offset})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {formData.scheduleTimezone && (
                          <p className="text-xs text-muted-foreground">
                            {t("campaigns.create.currentTimeInTimezone", "Current time in this timezone")}: {getCurrentTimeInTimezone(formData.scheduleTimezone)}
                          </p>
                        )}
                      </div>
                    )}

                </div>
              </div>

              {/* Future Scheduling Section - optional, and it reuses the timezone chosen above */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="schedule-enabled" className="text-base font-medium">
                      {t("campaigns.create.scheduleForLater", "Schedule for Later")}
                    </Label>
                    <InfoTooltip content={t("campaigns.create.scheduleTooltip", "Schedule this campaign to run at a specific date and time in the future.")} />
                  </div>
                  <Switch
                    id="schedule-enabled"
                    checked={formData.scheduleEnabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, scheduleEnabled: checked })}
                    data-testid="switch-schedule-enabled"
                  />
                </div>

                {formData.scheduleEnabled && (
                  <div className="space-y-2 pl-6 animate-in slide-in-from-top-2 duration-200">
                    <Label htmlFor="schedule-datetime">{t("campaigns.create.scheduleDateTime", "Schedule Date & Time")}</Label>
                    <Input
                      id="schedule-datetime"
                      type="datetime-local"
                      value={formData.scheduledDateTime}
                      onChange={(e) => setFormData({ ...formData, scheduledDateTime: e.target.value })}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full"
                      data-testid="input-schedule-datetime"
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.scheduleTimezone
                        ? t("campaigns.create.scheduleDateTimeHelp", "The campaign will automatically start at this time in the selected timezone.")
                        : t("campaigns.create.scheduleNeedsTimezone", "Choose a campaign timezone above so this time is unambiguous.")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t("campaigns.create.step2Title")}</h3>
              
              <div className="space-y-2">
                <Label htmlFor="agent-select">{t("campaigns.create.selectAgentRequired")}</Label>
                <Select value={formData.agentId} onValueChange={(value) => setFormData({ ...formData, agentId: value })}>
                  <SelectTrigger data-testid="select-agent">
                    <SelectValue placeholder={agents.filter(a => a.type === 'flow' && a.isActive !== false && (!a.voiceProvider || a.voiceProvider === 'elevenlabs' || a.voiceProvider === 'bulbul' || a.voiceProvider === 'nova_sonic' || a.voiceProvider === 'nova-sonic' || a.voiceProvider === 'gemini' || a.voiceProvider === 'kie-elevenlabs')).length === 0 ? t("campaigns.create.noAgentsAvailable") : t("campaigns.create.agentPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.filter(agent => {
                      // Only show flow agents with compatible voice providers (ElevenLabs, Bulbul, Nova Sonic, Gemini, or Kie.ai ElevenLabs)
                      // Azure, OpenAI don't support campaigns
                      const compatibleProvider = !agent.voiceProvider || agent.voiceProvider === 'elevenlabs' || agent.voiceProvider === 'bulbul' || agent.voiceProvider === 'nova_sonic' || agent.voiceProvider === 'nova-sonic' || agent.voiceProvider === 'gemini' || agent.voiceProvider === 'kie-elevenlabs';
                      return agent.type === 'flow' && agent.isActive !== false && compatibleProvider;
                    }).map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} - {agent.personality} ({t("agents.type.flow")})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {agents.filter(a => a.type === 'flow' && a.isActive !== false && (!a.voiceProvider || a.voiceProvider === 'elevenlabs' || a.voiceProvider === 'bulbul' || a.voiceProvider === 'nova_sonic' || a.voiceProvider === 'nova-sonic' || a.voiceProvider === 'gemini' || a.voiceProvider === 'kie-elevenlabs')).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t("campaigns.create.noAgentsAvailable")} Campaigns require agents with ElevenLabs, Nova Sonic, Gemini, or Kie.ai ElevenLabs voice provider.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="campaign-script">{t("campaigns.create.customScriptOptional")}</Label>
                  <InfoTooltip content={t("campaigns.create.scriptTooltip")} />
                </div>
                <Textarea
                  id="campaign-script"
                  placeholder={t("campaigns.create.scriptPlaceholder")}
                  value={formData.script}
                  onChange={(e) => setFormData({ ...formData, script: e.target.value })}
                  rows={4}
                  data-testid="input-campaign-script"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t("campaigns.create.step3Title")}</h3>
              
              <Tabs defaultValue="csv" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="csv" data-testid="tab-csv-upload">{t("campaigns.contacts.csvUpload")}</TabsTrigger>
                  <TabsTrigger value="manual" data-testid="tab-manual-entry">{t("campaigns.contacts.manualEntry")}</TabsTrigger>
                </TabsList>

                <TabsContent value="csv" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="contacts-csv">{t("campaigns.contacts.uploadCSVFile")}</Label>
                    <div className="border-2 border-dashed rounded-md p-6 text-center hover-elevate">
                      <input
                        id="contacts-csv"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => handleCsvFileChange(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="input-csv-file"
                      />
                      <label htmlFor="contacts-csv" className="cursor-pointer">
                        <Upload className={`h-8 w-8 mx-auto mb-2 ${csvValidationError ? 'text-red-500' : csvFile ? 'text-green-500' : 'text-muted-foreground'}`} />
                        {csvFile ? (
                          <div>
                            <p className="text-sm font-medium text-green-600">{csvFile.name}</p>
                            <p className="text-xs text-green-500 mt-1">✓ All required columns found</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium">{t("campaigns.contacts.clickToUploadCSV")}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              CSV or Excel (.xlsx) • {t("campaigns.contacts.includeColumns")}
                            </p>
                          </>
                        )}
                      </label>
                    </div>

                    {/* CSV Validation Error Display */}
                    {csvValidationError && (
                      <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border-2 border-red-300 dark:border-red-700">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="h-5 w-5 text-red-500" />
                          <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                            {csvValidationError}
                          </p>
                        </div>
                        {csvMissingColumns.length > 0 && (
                          <div className="ml-7">
                            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                              Missing columns:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {csvMissingColumns.map((col, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900 rounded text-red-800 dark:text-red-200 font-medium">
                                  {col}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Please download the template below and ensure all required columns are included.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-muted p-3 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">{t("campaigns.contacts.csvFormatExample")}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadTemplate}
                        className="text-xs text-primary hover:underline h-auto p-0"
                        data-testid="link-download-sample-csv"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        {flowVariables && (flowVariables.contactVariables?.length > 0)
                          ? t("campaigns.contacts.downloadCustomTemplate", "Download Custom Template")
                          : t("campaigns.contacts.downloadSampleCSV")}
                      </Button>
                    </div>

                    {/* Show required variables from flow if available (only contact-level) */}
                    {flowVariables && flowVariables.contactVariables?.length > 0 && (
                      <div className="mb-3 p-2 bg-violet-50 dark:bg-violet-950/30 rounded border border-violet-200 dark:border-violet-800">
                        <p className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-1">
                          Required columns for your agent's flow:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-900 rounded text-violet-800 dark:text-violet-200">phone_number</span>
                          <span className="text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-900 rounded text-violet-800 dark:text-violet-200">first_name</span>
                          <span className="text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-900 rounded text-violet-800 dark:text-violet-200">last_name</span>
                          {flowVariables.contactVariables.map((v, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900 rounded text-orange-800 dark:text-orange-200 font-medium">
                              {v.name}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Orange columns are per-contact variables from your flow.
                        </p>
                      </div>
                    )}

                    {/* Show captured variables info (collected during call) */}
                    {flowVariables && flowVariables.capturedVariables?.length > 0 && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200 dark:border-blue-800">
                        <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                          Variables collected during the call:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {flowVariables.capturedVariables.map((v, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200">
                              {v.name}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          These will be asked during the conversation - no need to provide upfront.
                        </p>
                      </div>
                    )}

                    <code className="text-xs block bg-background p-2 rounded">
                      {flowVariables && flowVariables.contactVariables?.length > 0 ? (
                        <>
                          phone_number,first_name,last_name{flowVariables.contactVariables.map(v => `,${v.name}`).join('')}<br />
                          +12345678900,John,Smith{flowVariables.contactVariables.map(() => ',Value').join('')}<br />
                        </>
                      ) : (
                        <>
                          phone_number,first_name,last_name,company,title<br />
                          +12345678900,John,Smith,ABC Corp,Manager<br />
                        </>
                      )}
                    </code>
                  </div>
                </TabsContent>

                <TabsContent value="manual" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="manual-first-name">{t("campaigns.contacts.firstNameRequired")}</Label>
                      <Input
                        id="manual-first-name"
                        placeholder={t("campaigns.contacts.firstNamePlaceholder")}
                        value={currentContact.firstName}
                        onChange={(e) => setCurrentContact({ ...currentContact, firstName: e.target.value })}
                        data-testid="input-manual-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manual-last-name">{t("campaigns.contacts.lastName")}</Label>
                      <Input
                        id="manual-last-name"
                        placeholder={t("campaigns.contacts.lastNamePlaceholder")}
                        value={currentContact.lastName}
                        onChange={(e) => setCurrentContact({ ...currentContact, lastName: e.target.value })}
                        data-testid="input-manual-last-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manual-phone">{t("campaigns.contacts.phoneRequired")}</Label>
                      <Input
                        id="manual-phone"
                        placeholder={t("campaigns.contacts.phonePlaceholder")}
                        value={currentContact.phone}
                        onChange={(e) => setCurrentContact({ ...currentContact, phone: e.target.value })}
                        data-testid="input-manual-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manual-email">{t("campaigns.contacts.email")}</Label>
                      <Input
                        id="manual-email"
                        type="email"
                        placeholder={t("campaigns.contacts.emailPlaceholder")}
                        value={currentContact.email}
                        onChange={(e) => setCurrentContact({ ...currentContact, email: e.target.value })}
                        data-testid="input-manual-email"
                      />
                    </div>
                  </div>

                  {/* Custom Fields Section */}
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-sm text-muted-foreground">
                      {t("campaigns.contacts.customFields", "Custom Fields (e.g., city, company)")}
                    </Label>

                    {/* Show optional personalization fields from flow (contact-level variables) */}
                    {flowVariables && flowVariables.contactVariables?.length > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-700 mb-3">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">i</span>
                          </div>
                          <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            Optional Personalization Fields
                          </p>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                          These fields can be added per contact for personalization. If not provided, the AI will adapt naturally.
                        </p>
                        <div className="space-y-3">
                          {flowVariables.contactVariables.map((v, i) => {
                            const value = currentContact.customFields?.[v.name] || '';
                            return (
                              <div key={i}>
                                <Label className="text-xs font-medium mb-1 block text-foreground">
                                  {v.name} <span className="text-muted-foreground">(optional)</span>
                                </Label>
                                <Input
                                  placeholder={`Enter ${v.name}`}
                                  value={value}
                                  onChange={(e) => setCurrentContact({
                                    ...currentContact,
                                    customFields: { ...currentContact.customFields, [v.name]: e.target.value }
                                  })}
                                  className="h-9"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Show captured variables info (collected during call) */}
                    {flowVariables?.capturedVariables?.length > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-700 mb-3">
                        <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                          Variables collected during the call:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {flowVariables.capturedVariables.map((v, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200">
                              {v.name}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          The AI will ask for these during the conversation.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        placeholder={t("campaigns.contacts.fieldName", "Field name (e.g., city)")}
                        value={customFieldKey}
                        onChange={(e) => setCustomFieldKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        className="flex-1"
                        data-testid="input-custom-field-key"
                      />
                      <Input
                        placeholder={t("campaigns.contacts.fieldValue", "Value")}
                        value={customFieldValue}
                        onChange={(e) => setCustomFieldValue(e.target.value)}
                        className="flex-1"
                        data-testid="input-custom-field-value"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (customFieldKey && customFieldValue) {
                            setCurrentContact({
                              ...currentContact,
                              customFields: { ...currentContact.customFields, [customFieldKey]: customFieldValue }
                            });
                            setCustomFieldKey("");
                            setCustomFieldValue("");
                          }
                        }}
                        data-testid="button-add-custom-field"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {Object.keys(currentContact.customFields || {}).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(currentContact.customFields || {}).map(([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs"
                          >
                            <span className="font-medium">{key}:</span> {value}
                            <button
                              type="button"
                              onClick={() => {
                                const newFields = { ...currentContact.customFields };
                                delete newFields[key];
                                setCurrentContact({ ...currentContact, customFields: newFields });
                              }}
                              className="ml-1 hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddContact}
                    className="w-full"
                    data-testid="button-add-contact"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("campaigns.contacts.addContact")}
                  </Button>

                  {manualContacts.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t("campaigns.contacts.addedContactsCount", { count: manualContacts.length })}</Label>
                      <div className="border rounded-md max-h-32 overflow-y-auto">
                        {manualContacts.map((contact, index) => (
                          <div key={index} className="flex items-center justify-between p-2 hover:bg-muted/50">
                            <div className="flex-1">
                              <span className="font-medium text-sm">{contact.firstName} {contact.lastName || ""}</span>
                              <span className="text-sm text-muted-foreground ml-2">{contact.phone}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveContact(index)}
                              data-testid={`button-remove-contact-${index}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <p className="text-sm text-muted-foreground">
                {t("campaigns.create.skipContactsInfo")}
              </p>
            </div>
          )}
        </div>
        </div>

        <div className="flex justify-between gap-2 px-6 py-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                {t("common.back")}
              </Button>
            )}
            {step < 3 ? (
              <Button onClick={handleNext} data-testid="button-next-step">
                {t("common.next")}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || !formData.agentId}
                data-testid="button-create-campaign"
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("campaigns.actions.createCampaign")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
      </Dialog>

      <PhoneConflictDialog
        open={conflictDialog.isOpen}
        onClose={() => setConflictDialog(initialPhoneConflictState)}
        title={conflictDialog.title}
        message={conflictDialog.message}
        conflictType={conflictDialog.conflictType}
        connectedAgentName={conflictDialog.connectedAgentName}
        campaignName={conflictDialog.campaignName}
      />
    </>
  );
}
